import { WATERMARK } from "constants/chartCommons";
import { PolarChart } from "store/slice/charts";
import { selectedLegendItems } from "../chart.functions";

export const getOptions = (
    series: Highcharts.SeriesOptionsType[],
    legendSelected: number[],
    renderYAxisTicks: (chart: PolarChart, isExporting?: boolean, tickSteps?: number) => void,
    renderOuterYAxisGrid: (chart: PolarChart) => void
): Highcharts.Options => {
    const labelDistance = 8;

    return {
        chart: {
            animation: false,
            className: "situationalChart",
            polar: true,
            type: "line",
            spacing: [0, 0, 0, 0],
            margin: [0, 0, 0, 0],
            events: {
                load: function (this) {
                    const chart = this as unknown as PolarChart;
                    renderYAxisTicks(chart);
                    renderOuterYAxisGrid(chart);
                },
                redraw: function (this) {
                    const chart = this as unknown as PolarChart;

                    if (!chart.credits || !chart.pane?.[0]?.center) return;

                    const { pane, chartWidth, chartHeight, xAxis } = chart;
                    const [paneX, paneY, diameter] = pane[0].center;
                    const creditsBBox = chart.credits.getBBox();
                    const thirdxAxisLabel = xAxis[0].ticks[2]?.label?.getBBox();

                    chart.credits.attr({
                        x: paneX - creditsBBox.width / 2,
                        y: paneY + diameter / 2 + (thirdxAxisLabel?.height ?? 0) + creditsBBox.height + 10,
                    });

                    renderYAxisTicks(chart);
                    renderOuterYAxisGrid(chart);

                    // Initial position of legend (when legend is below the chart)
                    const legendGroup = chart.legend.group;

                    const IS_MIN_DIMENSIONS_DIFF =
                        chartWidth / chartHeight <=
                        Math.min(chartWidth, chartHeight) / Math.max(chartWidth, chartHeight);

                    if (IS_MIN_DIMENSIONS_DIFF) {
                        legendGroup.attr({
                            translateY: paneY + diameter / 2 + creditsBBox.height + 50,
                        });
                    }
                },
            },
        },
        pane: {
            size: "72%",
            center: ["50%", "42%"],
        },
        credits: {
            text: WATERMARK,
            href: "#",
            position: {
                align: "left",
            },
            style: {
                fontSize: "8px",
            },
        },
        title: {
            text: undefined,
        },
        xAxis: {
            gridLineWidth: 0,
            tickmarkPlacement: "on",
            lineWidth: 0,
            labels: {
                useHTML: true,
                style: {
                    fontSize: "10px",
                    color: "#84acbc",
                    whiteSpace: "inherit",
                },
                formatter: function () {
                    if (this.pos === 0) {
                        return `<span style="width: 100px; display: inline-block; text-align: center; margin-top: -12px;">${this.value}</span>`;
                    }
                    if (this.pos === 1) {
                        return `<span style="width: 100px; display: inline-block; text-align: left; white-space: break-spaces;">${this.value}</span>`;
                    }
                    if (this.pos === 2) {
                        return `<span style="width: 120px; display: inline-block; text-align: center; white-space: break-spaces;">${this.value}</span>`;
                    }
                    if (this.pos === 3) {
                        return `<span style="width: auto; display: inline-block; text-align: right; white-space: break-spaces;">${this.value}</span>`;
                    }
                    return `${this.value}`;
                },
            },
        },
        yAxis: {
            gridLineInterpolation: "polygon",
            gridLineColor: "#84acbc",
            min: 0,
            max: 100,
            showLastLabel: "true",
            tickPositions: [0, 20, 40, 60, 80, 100],
            labels: {
                enabled: false,
            },
        },
        tooltip: {
            enabled: true,
            pointFormat: "",
            padding: 0,
            backgroundColor: "#000000",
            borderWidth: 0,
            shared: false,
            useHTML: true,
            style: {
                color: "white",
                fontSize: "11px",
            },
        },
        legend: {
            verticalAlign: "bottom",
            itemWidth: 200,
            margin: 20,
        },
        plotOptions: {
            series: {
                marker: {
                    enabled: false,
                },
                enableMouseTracking: true,
                events: {
                    legendItemClick: function (this: Highcharts.Series) {
                        selectedLegendItems(this, legendSelected);
                    },
                },
            },
        },
        series,
        exporting: {
            menuItemDefinitions: {
                downloadPNG: { text: "Download PNG Image" },
                downloadSVG: { text: "Download SVG Image" },
            },
            buttons: {
                contextButton: {
                    menuItems: ["downloadPNG", "downloadSVG"],
                },
            },
            allowHTML: true,
            chartOptions: {
                pane: {
                    size: "80%",
                },
                chart: {
                    events: {
                        load: function (this) {
                            const chart = this as unknown as PolarChart;
                            renderYAxisTicks(chart, true);

                            if (!chart.credits || !chart.pane?.[0]?.center) return;

                            const [paneX, paneY, diameter] = chart.pane[0].center;
                            const creditsBBox = chart.credits.getBBox();

                            chart.credits.attr({
                                x: paneX - creditsBBox.width / 2,
                                y: paneY + diameter / 2 + labelDistance * 2 + creditsBBox.height + 10,
                            });
                        },
                    },
                },
            },
        },
    };
};
